import { useCallback, useEffect, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Icon,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useToast
} from '@chakra-ui/react'
import { Form } from 'react-final-form'
import { GiftIcon } from '@heroicons/react/24/outline'
import { useIntl } from 'react-intl'
import BasePageLayout from '../../../common/components/BasePageLayout'
import PageHeader from '../../../../../components/InternalAppShell/PageHeader'
import InputField from '../../../../../components/common/form/InputField'
import { getReferralService } from '../../../../../services/Services'
import { useAuthState } from '../../../../../auth/AuthProvider'
import { useAccount } from '../../../account/AccountProvider'
import {
  type ReferralStatus,
  type ReferralType
} from '../../../../../model/referral'
import { trackEvent } from '../../../../../services/analytics'
import { makeAlphaDate } from '../../../../../utils/dateUtils'
import { REFERRAL_REWARD_DAYS } from './constants'
import {
  validateReferralForm,
  type ReferralFormValues
} from './validateReferralForm'
import messages from './messages'

const statusColorScheme: Record<ReferralStatus, string> = {
  pending: 'yellow',
  rewarded: 'green',
  rejected: 'gray'
}

const cardProps = {
  p: 6,
  bg: 'white',
  boxShadow: 'none',
  borderRadius: 'xl',
  borderWidth: '1px',
  borderColor: 'gray.200'
} as const

export default function Referrals() {
  const toast = useToast()
  const { formatMessage } = useIntl()

  // `/referrals` sits behind GuardedRoute, which bounces secretaries to
  // `/secretary`, so only a doctor renders this page and uid === doctorId.
  const { uid, email } = useAuthState()
  const { phone } = useAccount()

  const [referrals, setReferrals] = useState<ReferralType[]>([])
  const [loadingReferrals, setLoadingReferrals] = useState(true)

  useEffect(() => {
    if (!uid) return

    setLoadingReferrals(true)
    getReferralService()
      .getReferrals()
      .then((result) => {
        setReferrals(result)
      })
      .catch(() => {
        setReferrals([])
      })
      .finally(() => {
        setLoadingReferrals(false)
      })
  }, [uid])

  // Errors come back as message keys, so the pure validator stays i18n-free.
  const validate = useCallback(
    (values: ReferralFormValues) => {
      const errors = validateReferralForm(values, {
        ownEmail: email ?? '',
        ownPhone: phone ?? '',
        existing: referrals
      })

      return Object.entries(errors).reduce<Record<string, string>>(
        (acc, [field, key]) => {
          acc[field] = formatMessage(messages[key as keyof typeof messages])
          return acc
        },
        {}
      )
    },
    [email, phone, referrals, formatMessage]
  )

  const handleSubmit = async (
    values: ReferralFormValues,
    reset: () => void
  ): Promise<void> => {
    const refereeEmail = values.refereeEmail ?? ''
    const refereePhone = values.refereePhone ?? ''

    try {
      const created = await getReferralService().createReferral({
        refereeName: values.refereeName ?? '',
        refereeEmail,
        refereePhone
      })

      setReferrals((current) => [created, ...current])
      reset()

      // Booleans only — no colleague PII leaves for analytics.
      trackEvent('referral_submitted', {
        has_email: Boolean(refereeEmail.trim()),
        has_phone: Boolean(refereePhone.trim())
      })

      toast({
        title: formatMessage(messages.referralSentSuccess),
        status: 'success',
        duration: 5000,
        isClosable: true
      })
    } catch {
      toast({
        title: formatMessage(messages.referralSentError),
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  // Driven by the doc rather than the constant, so a badge can never claim a
  // reward the team did not actually grant.
  const renderStatus = (referral: ReferralType) => {
    if (referral.status === 'rewarded') {
      return formatMessage(messages.statusRewarded, {
        days: referral.rewardDays
      })
    }
    if (referral.status === 'rejected') {
      return formatMessage(messages.statusRejected)
    }
    return formatMessage(messages.statusPending)
  }

  // Extracted rather than inlined as a JSX ternary: prettier and this repo's
  // eslint `multiline-ternary` rule disagree on how to format one, and each
  // undoes the other.
  const renderReferralList = () => {
    if (loadingReferrals) {
      return (
        <Flex justify="center" py={6}>
          <Spinner size="sm" color="gray.400" data-testid="referrals-loading" />
        </Flex>
      )
    }

    if (referrals.length === 0) {
      return <Text color="gray.500">{formatMessage(messages.noReferrals)}</Text>
    }

    return referrals.map((referral) => (
      <Flex key={referral.id} justify="space-between" align="center" gap={4}>
        <Box minW={0}>
          <Text fontWeight="650" noOfLines={1}>
            {referral.refereeName}
          </Text>
          <Text color="gray.500" fontSize="sm" noOfLines={1}>
            {referral.refereeEmail || referral.refereePhone} &middot;{' '}
            {makeAlphaDate(referral.createdAt)}
          </Text>
        </Box>
        <Badge flexShrink={0} colorScheme={statusColorScheme[referral.status]}>
          {renderStatus(referral)}
        </Badge>
      </Flex>
    ))
  }

  // Counted from the list already in hand — no extra query. `rewardDays` is
  // whatever the team actually granted, which need not be the current constant.
  const pendingCount = referrals.filter((r) => r.status === 'pending').length
  const daysEarned = referrals.reduce((total, r) => total + r.rewardDays, 0)

  const renderStat = (label: string, value: number) => (
    <Box {...cardProps} p={4}>
      <Text fontSize="xs" color="gray.500">
        {label}
      </Text>
      <Text fontSize="2xl" fontWeight="650" letterSpacing="-0.02em">
        {value}
      </Text>
    </Box>
  )

  return (
    <BasePageLayout
      isCentered
      contentMode="wide"
      title={formatMessage(messages.pageTitle)}
    >
      <PageHeader
        title={formatMessage(messages.pageTitle)}
        description={formatMessage({ id: 'page.referralsDescription' })}
        icon={GiftIcon}
      />

      <Box {...cardProps}>
        <Flex align="center" gap={3}>
          <Flex
            w="36px"
            h="36px"
            flex="0 0 auto"
            align="center"
            justify="center"
            borderRadius="10px"
            bg="gray.100"
          >
            <Icon as={GiftIcon} boxSize="18px" color="gray.700" />
          </Flex>
          <Box>
            <Heading size="md" fontWeight="semibold">
              {formatMessage(messages.sectionHeading)}
            </Heading>
            <Text color="gray.500" fontSize="sm">
              {formatMessage(messages.rewardExplainer, {
                days: REFERRAL_REWARD_DAYS
              })}
            </Text>
          </Box>
        </Flex>
      </Box>

      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>
        {renderStat(formatMessage(messages.statTotal), referrals.length)}
        {renderStat(formatMessage(messages.statPending), pendingCount)}
        {renderStat(formatMessage(messages.statDaysEarned), daysEarned)}
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} gap={5} alignItems="start">
        <Box {...cardProps}>
          <Stack spacing={4}>
            <Heading size="sm">{formatMessage(messages.formHeading)}</Heading>
            <Divider />

            <Form<ReferralFormValues>
              validate={validate}
              onSubmit={async (values, form) => {
                await handleSubmit(values, () => {
                  form.restart()
                })
              }}
              render={({ handleSubmit: onSubmit, submitting }) => (
                <form onSubmit={onSubmit}>
                  <Stack spacing={4}>
                    <InputField
                      name="refereeName"
                      label={formatMessage(messages.refereeName)}
                    />
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <InputField
                        name="refereeEmail"
                        label={formatMessage(messages.refereeEmail)}
                      />
                      <InputField
                        type="tel"
                        name="refereePhone"
                        label={formatMessage(messages.refereePhone)}
                      />
                    </SimpleGrid>
                    <Text fontSize="sm" color="gray.500">
                      {formatMessage(messages.contactHint)}
                    </Text>
                    <Flex justify="flex-end">
                      <Button
                        type="submit"
                        isLoading={submitting}
                        loadingText={formatMessage(messages.sendingText)}
                      >
                        {formatMessage(messages.sendReferral)}
                      </Button>
                    </Flex>
                  </Stack>
                </form>
              )}
            />
          </Stack>
        </Box>

        <Box {...cardProps}>
          <Stack spacing={4}>
            <Heading size="sm">{formatMessage(messages.yourReferrals)}</Heading>
            <Divider />
            <Stack spacing={3}>{renderReferralList()}</Stack>
          </Stack>
        </Box>
      </SimpleGrid>
    </BasePageLayout>
  )
}
